/*
 *
 * TriggerTimeline Sagas
 *
 * @version 1.0.0
 * @author Patrizia Petrera
 * @since 11-Mar-2025
 */

import { put, fork, take, call, all, select } from 'redux-saga/effects'
import { push } from 'redux-first-history'
import * as api from 'Api/triggerTimeline/triggerTimeline'
import * as comunicationsApi from 'Api/comunications/comunications'
import * as kitStartApi from 'Api/kitStart/kitStart'
import * as userProfilingActions from 'Store/actions/userProfiling'
import { nullSafe } from 'Utility/globalMethods'
import * as actions from 'Store/actions/triggerTimeline'
import * as popupActions from 'Store/actions/popup'
import { doShowTtl, doHideTtl } from 'Store/actions/triggerTimeline'
import { elementsMap } from 'Utility/TTLmapping'
import { roles } from 'Utility/utils'
import { AML_STATUSES } from 'Route/Aml/modules/AmlConstants'

const getPathname = (state) => nullSafe(() => state.router.location.pathname, '')
const getPreviousLocation = (state) => nullSafe(() => state.router.previousLocations[1].location.pathname, '')
const getRecallTTL = (state) => nullSafe(() => state.triggerTimeline.recallTTL, false)
const getUserRole = (state) => nullSafe(() => state.userProfiling.profiling.user.userRole, '')
const getHasBlockingAml = (state) =>
  [AML_STATUSES.BLOCKED, AML_STATUSES.ADMIN_BLOCKED].includes(nullSafe(() => state.userProfiling.amlStatusInfo.status, ''))

// @generator saga

export function * doRetrieveTTL () {
  if (yield select(getHasBlockingAml)) {
    yield put(doShowTtl('AML'))
    return
  }

  yield put(actions.retrieveTTL.request({}, { loading: true }))
  const { response, error } = yield call(api.doRetrieveTTL, {})
  if (!error) {
    yield put(actions.retrieveTTL.success(response.data))
    // A pending TTL must not replace the restored block or overlap its OTP flow.
    if (yield select(getHasBlockingAml)) return
    const triggerTimeline = nullSafe(() => response.data.triggerTimeline.id, '')
    if (triggerTimeline && Object.keys(elementsMap).includes(triggerTimeline)) {
      const triggerTimelineAdditipnalInfo = nullSafe(() => response.data.triggerTimeline.additionalInfo, {})
      if (triggerTimeline === 'NEWS' && triggerTimelineAdditipnalInfo.category === 'AML_REMINDER') {
        const userRole = yield select(getUserRole)
        if (!Object.values(roles).includes(userRole)) return
      }
      yield put(doShowTtl(triggerTimeline, triggerTimelineAdditipnalInfo))
    }
  } else {
    yield put(actions.retrieveTTL.failure(error, { hideErrorPopup: true, ...response }))
  }
}

export function * setRetrieveTTLread (payload) {
  const id = (payload && payload.id) || ''
  yield put(actions.setRetrieveTTLread.request({ id }, { loading: true }))
  const { response, error } = yield call(api.setRetrieveTTLread, { id })
  if (!error) {
    yield put(actions.setRetrieveTTLread.success(response.data))
  } else {
    yield put(actions.setRetrieveTTLread.failure(error, response))
    yield put(doHideTtl({ recallTTL: true }))
  }
}

export function * markNewsReadSaga (payload) {
  const id = (payload && payload.id) || ''
  const retrieveNextTtl = Boolean(payload && payload.retrieveNextTtl)
  yield put(actions.markNewsRead.request({ id }))
  const { response, error } = yield call(comunicationsApi.getComunicationDetail, { id })

  if (!error) {
    yield put(actions.markNewsRead.success(response.data))
    if (retrieveNextTtl) {
      yield put(actions.doRetrieveTTL())
    }
  } else {
    yield put(actions.markNewsRead.failure(error, response))
  }
}

function * getComunicationDetailSaga (payload) {
  const id = (payload && payload.id) || ''
  yield put(actions.getComunicationDetail.request({ id }, { loading: true }))
  const { error, response } = yield call(comunicationsApi.getComunicationDetail, { id })

  if (!error) {
    yield put(actions.getComunicationDetail.success(response.data))
    yield put(doHideTtl())
    yield put(push('/profile/comunications/detail'))
  } else {
    yield put(actions.getComunicationDetail.failure(error, response))
  }
}

function * getKitStartDataSaga (payload) {
  yield put(actions.getKitStartData.request({ ...payload }, { loading: true }))
  const { error, response } = yield call(kitStartApi.startKitStart, { ...payload })
  if (!error) {
    yield put(actions.getKitStartData.success(response.data))
  } else {
    yield put(actions.getKitStartData.failure(error, response))
  }
}

// @generator saga:watch
function * watchUserProfileSuccess () {
  while (true) {
    yield take(userProfilingActions.actionTypes.PROFILING_SUCCESS)
    const pathname = yield select(getPathname)
    const hasBlockingAml = yield select(getHasBlockingAml)
    if (pathname === '/' || hasBlockingAml) {
      yield fork(doRetrieveTTL)
    }
  }
}

function * watchTTLRefreshPage () {
  while (true) {
    const { payload } = yield take('@@router/LOCATION_CHANGE')
    const pathname = nullSafe(() => payload.location.pathname, '')
    const previousLocation = yield select(getPreviousLocation)
    if (pathname === '/' && previousLocation !== '') {
      yield fork(doRetrieveTTL)
    }
  }
}

function * watchRetrieveTTLSaga () {
  while (
    yield take(actions.actionTypes.DO_RETRIEVE_TTL)) {
    yield fork(doRetrieveTTL)
  }
}

function * watchGetComunicationDetailSaga () {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_GET_COMUNICATION_DETAIL)
    yield call(getComunicationDetailSaga, payload)
  }
}

function * watchSetRetrieveTTLreadSaga () {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_SET_RETRIEVE_TTL_READ)
    yield call(setRetrieveTTLread, payload)
  }
}

function * watchMarkNewsReadSaga () {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_MARK_NEWS_READ)
    yield call(markNewsReadSaga, payload)
  }
}

function * watchGetKitStartDataSaga () {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_GET_KITSTART_DATA)
    yield call(getKitStartDataSaga, payload)
  }
}

function * watchRecallTTLAfterErrorSaga () {
  while (yield take(popupActions.actionTypes.DO_HIDE_POPUP)) {
    const recallTTL = yield select(getRecallTTL)
    if (recallTTL) {
      yield fork(doRetrieveTTL)
    }
  }
}

/**
 * Root saga manages watcher lifecycle
 */
export default function * rootSaga () {
  yield all([
    watchUserProfileSuccess(),
    watchTTLRefreshPage(),
    watchRetrieveTTLSaga(),
    watchGetComunicationDetailSaga(),
    watchSetRetrieveTTLreadSaga(),
    watchMarkNewsReadSaga(),
    watchGetKitStartDataSaga(),
    watchRecallTTLAfterErrorSaga()
  ])
}
